<!--
/**
 * APCWO customisation for OrangeHRM — Email Templates module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <oxd-form :loading="loading" @submit-valid="onSubmit">
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.name"
            label="Name"
            :rules="rules.name"
            required
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.eventType"
            type="select"
            label="Event type"
            :options="eventTypeOptions"
            :rules="rules.eventType"
            :show-empty-selector="false"
            required
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <oxd-form-row>
      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.category"
            label="Category"
            :rules="rules.category"
            required
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.subCategory"
            label="Sub category"
            :rules="rules.subCategory"
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.status"
            type="select"
            label="Status"
            :options="statusOptions"
            :show-empty-selector="false"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <oxd-divider />

    <oxd-form-row>
      <oxd-input-field
        v-model="form.subject"
        label="Subject"
        :rules="rules.subject"
        required
      />
    </oxd-form-row>

    <oxd-form-row>
      <div class="apcwo-email-template-form__editor-head">
        <oxd-text tag="p" class="apcwo-email-template-form__label">
          HTML body *
        </oxd-text>
        <merge-field-picker @insert="insertToken" />
      </div>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <textarea
            ref="bodyInput"
            v-model="form.bodyHtml"
            class="apcwo-email-template-form__textarea"
            spellcheck="false"
            rows="16"
          ></textarea>
          <oxd-text
            v-if="bodyError"
            tag="p"
            class="apcwo-email-template-form__error"
          >
            {{ bodyError }}
          </oxd-text>
        </oxd-grid-item>
        <oxd-grid-item>
          <html-preview
            :subject="form.subject"
            :body-html="form.bodyHtml"
            :event-type="eventTypeId"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <oxd-divider />

    <oxd-text tag="p" class="apcwo-email-template-form__section">
      Recipients &amp; sender
    </oxd-text>
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.fromEmail"
            label="From email"
            :rules="rules.email"
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.fromName"
            label="From name"
            :rules="rules.shortText"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>
    <oxd-form-row>
      <oxd-input-field
        v-model="form.toList"
        label="To (comma separated)"
        :rules="rules.addressList"
      />
    </oxd-form-row>
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.ccList"
            label="Cc (comma separated)"
            :rules="rules.addressList"
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.bccList"
            label="Bcc (comma separated)"
            :rules="rules.addressList"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.replyToEmail"
            label="Reply-to email"
            :rules="rules.email"
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="form.replyToName"
            label="Reply-to name"
            :rules="rules.shortText"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <oxd-divider />

    <oxd-text tag="p" class="apcwo-email-template-form__section">
      Send a test
    </oxd-text>
    <oxd-form-row>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="testEmail"
            label="Send test email to"
            :rules="rules.email"
          />
        </oxd-grid-item>
        <oxd-grid-item class="apcwo-email-template-form__test-action">
          <oxd-button
            type="button"
            display-type="secondary"
            label="Send test email"
            :disabled="sendingTest"
            @click="onSendTest"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <oxd-divider />

    <oxd-form-actions>
      <required-text />
      <oxd-button
        type="button"
        display-type="ghost"
        label="Cancel"
        @click="$emit('cancel')"
      />
      <oxd-button type="submit" display-type="secondary" :label="submitLabel" />
    </oxd-form-actions>
  </oxd-form>
</template>

<script>
import {
  required,
  shouldNotExceedCharLength,
  validEmailFormat,
} from '@ohrm/core/util/validation/rules';
import {APIService} from '@ohrm/core/util/services/api.service';
import MergeFieldPicker from './MergeFieldPicker.vue';
import HtmlPreview from './HtmlPreview.vue';
import {insertAtCaret} from '../util/caret-insert';

const EVENT_TYPES = [
  {id: 'birthday', label: 'Birthday'},
  {id: 'work_anniversary', label: 'Work Anniversary'},
  {id: 'public_holiday', label: 'Public Holiday'},
  {id: 'generic', label: 'Generic'},
];
const STATUSES = [
  {id: 'draft', label: 'Draft'},
  {id: 'active', label: 'Active'},
  {id: 'inactive', label: 'Inactive'},
];

const asOption = (list, value, fallback) => {
  const found = list.find((o) => o.id === value);
  return found || fallback;
};

export default {
  name: 'EmailTemplateForm',
  components: {
    'merge-field-picker': MergeFieldPicker,
    'html-preview': HtmlPreview,
  },
  props: {
    initialValue: {
      type: Object,
      default: () => ({}),
    },
    submitLabel: {
      type: String,
      default: 'Save',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    bodyError: {
      type: String,
      default: null,
    },
  },
  emits: ['submit', 'cancel'],
  setup() {
    const testHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/apcwo/email-templates/test',
    );
    testHttp.setIgnorePath('/api/v2/apcwo/email-templates/test');
    return {testHttp};
  },
  data() {
    const v = this.initialValue || {};
    return {
      eventTypeOptions: EVENT_TYPES,
      statusOptions: STATUSES,
      testEmail: '',
      sendingTest: false,
      form: {
        name: v.name || '',
        category: v.category || '',
        subCategory: v.subCategory || '',
        eventType: asOption(EVENT_TYPES, v.eventType, EVENT_TYPES[0]),
        status: asOption(STATUSES, v.status, STATUSES[0]),
        subject: v.subject || '',
        bodyHtml: v.bodyHtml || '',
        fromEmail: v.fromEmail || '',
        fromName: v.fromName || '',
        toList: v.toList || '',
        ccList: v.ccList || '',
        bccList: v.bccList || '',
        replyToEmail: v.replyToEmail || '',
        replyToName: v.replyToName || '',
      },
      rules: {
        name: [required, shouldNotExceedCharLength(150)],
        category: [required, shouldNotExceedCharLength(100)],
        subCategory: [shouldNotExceedCharLength(100)],
        eventType: [required],
        subject: [required, shouldNotExceedCharLength(255)],
        email: [(v) => v === '' || v == null || validEmailFormat(v)],
        shortText: [shouldNotExceedCharLength(150)],
        addressList: [
          shouldNotExceedCharLength(1000),
          (value) => this.validateAddressList(value),
        ],
      },
    };
  },
  computed: {
    eventTypeId() {
      return this.form.eventType ? this.form.eventType.id : null;
    },
  },
  created() {
    this.testHttp
      .getAll()
      .then((response) => {
        this.testEmail = response.data.data.defaultTestEmail || '';
      })
      .catch(() => {
        // no default available — leave the field blank
      });
  },
  methods: {
    validateAddressList(value) {
      if (!value) return true;
      const parts = String(value)
        .split(/[,;\n]+/)
        .map((p) => p.trim())
        .filter((p) => p !== '');
      const ok = parts.every((p) => validEmailFormat(p) === true);
      return ok || 'Enter valid, comma-separated email addresses';
    },
    insertToken(placeholder) {
      const el = this.$refs.bodyInput;
      const caret = el ? el.selectionStart : null;
      const {value, caret: nextCaret} = insertAtCaret(
        this.form.bodyHtml,
        placeholder,
        caret,
      );
      this.form.bodyHtml = value;
      this.$nextTick(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(nextCaret, nextCaret);
        }
      });
    },
    onSubmit() {
      this.$emit('submit', {
        name: this.form.name,
        category: this.form.category,
        subCategory: this.form.subCategory || null,
        eventType: this.form.eventType ? this.form.eventType.id : 'generic',
        status: this.form.status ? this.form.status.id : 'draft',
        subject: this.form.subject,
        bodyHtml: this.form.bodyHtml,
        fromEmail: this.form.fromEmail || null,
        fromName: this.form.fromName || null,
        toList: this.form.toList || null,
        ccList: this.form.ccList || null,
        bccList: this.form.bccList || null,
        replyToEmail: this.form.replyToEmail || null,
        replyToName: this.form.replyToName || null,
      });
    },
    onSendTest() {
      if (!this.testEmail || validEmailFormat(this.testEmail) !== true) {
        this.$toast.error({
          title: 'Error',
          message: 'Enter a valid email address to send the test to',
        });
        return;
      }
      this.sendingTest = true;
      this.testHttp
        .create({
          testEmail: this.testEmail,
          subject: this.form.subject,
          bodyHtml: this.form.bodyHtml,
          eventType: this.eventTypeId || undefined,
          fromEmail: this.form.fromEmail || undefined,
          fromName: this.form.fromName || undefined,
          replyToEmail: this.form.replyToEmail || undefined,
          replyToName: this.form.replyToName || undefined,
        })
        .then((response) => {
          this.$toast.success({
            title: 'Sent',
            message: `Test email sent to ${response.data.data.sentTo}`,
          });
        })
        .catch((e) => {
          const message = e?.response?.data?.error?.message;
          this.$toast.error({
            title: 'Error',
            message: message || 'Test email could not be sent',
          });
        })
        .finally(() => {
          this.sendingTest = false;
        });
    },
  },
};
</script>

<style scoped>
.apcwo-email-template-form__editor-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 6px;
}
.apcwo-email-template-form__label,
.apcwo-email-template-form__section {
  font-weight: 700;
  font-size: 13px;
}
.apcwo-email-template-form__section {
  margin: 8px 0;
}
.apcwo-email-template-form__test-action {
  display: flex;
  align-items: flex-end;
}
.apcwo-email-template-form__textarea {
  width: 100%;
  min-height: 320px;
  padding: 10px;
  font-family: monospace;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid #d9dbdd;
  border-radius: 6px;
  resize: vertical;
}
.apcwo-email-template-form__error {
  margin-top: 4px;
  font-size: 12px;
  color: #e03c3c;
}
</style>
